import { useState, useEffect, useRef, useCallback } from 'react'
import { incomingNotifAPI } from '../../services/api'
import useCRMStore from '../../store/useCRMStore'

// Inject glow keyframes
const glowStyles = document.createElement('style')
glowStyles.textContent = `
  @keyframes glow-pulse-primary {
    0%, 100% { box-shadow: 0 0 4px 1px rgba(59,130,246,0.3), inset 0 0 4px 0 rgba(59,130,246,0.05); }
    50% { box-shadow: 0 0 12px 4px rgba(59,130,246,0.5), inset 0 0 8px 0 rgba(59,130,246,0.1); }
  }
  @keyframes glow-pulse-amber {
    0%, 100% { box-shadow: 0 0 4px 1px rgba(245,158,11,0.3), inset 0 0 4px 0 rgba(245,158,11,0.05); }
    50% { box-shadow: 0 0 12px 4px rgba(245,158,11,0.5), inset 0 0 8px 0 rgba(245,158,11,0.1); }
  }
  .notif-glow-mio { animation: glow-pulse-primary 2s ease-in-out infinite; }
  .notif-glow-nuevo { animation: glow-pulse-amber 2s ease-in-out infinite; }
`
if (!document.querySelector('#notif-glow-styles')) {
  glowStyles.id = 'notif-glow-styles'
  document.head.appendChild(glowStyles)
}

const POLL_INTERVAL = 10000 // 10 seconds
const SOUND_ENABLED = true

// Simple notification sound using Web Audio API
function playNotifSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = 880
    osc.type = 'sine'
    gain.gain.value = 0.08
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3)
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.3)
  } catch (_) {}
}

function timeAgo(dateStr) {
  const now = new Date()
  const then = new Date(dateStr)
  const diffMs = now - then
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'ahora'
  if (diffMin < 60) return `hace ${diffMin}m`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `hace ${diffH}h`
  return `hace ${Math.floor(diffH / 24)}d`
}

export default function IncomingNotifications({ user, onNavigateToContact }) {
  const [notifications, setNotifications] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [prevCount, setPrevCount] = useState(0)
  const popupRef = useRef(null)
  const btnRef = useRef(null)

  const readAtKey = `notif_read_at_${user?.phone || 'default'}`

  const getReadAt = useCallback(() => {
    try {
      return localStorage.getItem(readAtKey) || '1970-01-01T00:00:00Z'
    } catch { return '1970-01-01T00:00:00Z' }
  }, [readAtKey])

  const markAllRead = useCallback(() => {
    localStorage.setItem(readAtKey, new Date().toISOString())
  }, [readAtKey])

  // Poll for notifications
  useEffect(() => {
    let mounted = true
    const poll = async () => {
      try {
        const res = await incomingNotifAPI.getNotifications()
        if (mounted && res.notifications) {
          setNotifications(res.notifications)
        }
      } catch (_) {}
    }
    poll()
    const id = setInterval(poll, POLL_INTERVAL)
    return () => { mounted = false; clearInterval(id) }
  }, [])

  // Count unread
  const readAt = getReadAt()
  const unreadCount = notifications.filter(n => new Date(n.lastMessageTime) > new Date(readAt)).length

  // Play sound when new unread arrives
  useEffect(() => {
    if (SOUND_ENABLED && unreadCount > prevCount && prevCount >= 0) {
      playNotifSound()
    }
    setPrevCount(unreadCount)
  }, [unreadCount])

  // Mark read when popup closes (so glow stays visible while viewing)
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (!isOpen && wasOpenRef.current) {
      markAllRead()
    }
    wasOpenRef.current = isOpen
  }, [isOpen])

  // Close popup on outside click
  useEffect(() => {
    if (!isOpen) return
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  const handleClickNotif = (phone) => {
    setIsOpen(false)
    if (onNavigateToContact) onNavigateToContact(phone)
  }

  const isOwn = (n) => n.assignedAgent === user?.name

  // Collapsed: show up to 3 most recent
  const preview = notifications.slice(0, 3)

  if (notifications.length === 0) {
    return (
      <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500 text-lg">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        <span className="hidden lg:inline">Sin actividad reciente</span>
      </div>
    )
  }

  return (
    <div className="relative flex items-center gap-2">
      {/* Preview cards - up to 3 */}
      <div className="hidden md:flex items-center gap-1.5">
        {preview.map(n => {
          const own = isOwn(n)
          const unread = new Date(n.lastMessageTime) > new Date(readAt)
          return (
            <button
              key={n.phone}
              onClick={() => handleClickNotif(n.phone)}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-full text-base font-semibold transition-all duration-300 max-w-[180px] truncate cursor-pointer
                ${own && unread
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white notif-glow-mio ring-1 ring-primary-300 dark:ring-primary-500/50'
                  : !n.assignedAgent && unread
                    ? 'bg-gradient-to-r from-amber-400 to-amber-500 text-white notif-glow-nuevo ring-1 ring-amber-300 dark:ring-amber-400/50'
                  : unread
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 shadow-sm'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }
              `}
              title={`${n.name} (${n.phone}) - ${n.lastMessageText}`}
            >
              <span className="truncate">{n.name?.split(' ')[0] || n.phone}</span>
              <span className="text-sm font-medium whitespace-nowrap text-gray-700 dark:text-gray-300">{timeAgo(n.lastMessageTime)}</span>
            </button>
          )
        })}
      </div>

      {/* Bell button with badge */}
      <button
        ref={btnRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`
          relative p-1.5 rounded-lg transition-all duration-200 cursor-pointer
          ${isOpen
            ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300'
            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }
        `}
        title="Notificaciones de mensajes entrantes"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Expanded popup */}
      {isOpen && (
        <div
          ref={popupRef}
          className="absolute top-full mt-2 right-0 w-96 bg-white dark:bg-surface-dark-alt border border-gray-200 dark:border-surface-dark-border rounded-xl shadow-2xl z-50 overflow-hidden"
        >
          <div className="px-3 py-2 border-b border-gray-100 dark:border-surface-dark-border flex items-center justify-between">
            <span className="text-lg font-semibold text-gray-700 dark:text-gray-200">Mensajes entrantes</span>
            <span className="text-sm text-gray-400">{notifications.length} contacto{notifications.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.map(n => {
              const own = isOwn(n)
              const unread = new Date(n.lastMessageTime) > new Date(readAt)
              return (
                <button
                  key={n.phone}
                  onClick={() => handleClickNotif(n.phone)}
                  className={`
                    w-full text-left px-4 py-3 border-b border-gray-50 dark:border-surface-dark-border/50 transition-all duration-200 cursor-pointer
                    hover:bg-gray-50 dark:hover:bg-surface-dark-hover
                    ${own && unread
                      ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-l-primary-500 notif-glow-mio rounded-r-lg'
                      : !n.assignedAgent && unread
                        ? 'bg-amber-50 dark:bg-amber-900/15 border-l-4 border-l-amber-400 notif-glow-nuevo rounded-r-lg'
                      : unread
                        ? 'bg-amber-50/50 dark:bg-amber-900/10 border-l-4 border-l-amber-400'
                        : 'border-l-4 border-l-transparent'
                    }
                  `}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-lg font-semibold truncate ${own && unread ? 'text-primary-700 dark:text-primary-300' : 'text-gray-800 dark:text-gray-200'}`}>
                          {n.name || n.phone}
                        </span>
                        {own && (
                          <span className="text-xs px-2 py-0.5 rounded bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300 font-semibold whitespace-nowrap">MIO</span>
                        )}
                        {!n.assignedAgent && (
                          <span className="text-xs px-2 py-0.5 rounded bg-amber-200 dark:bg-amber-800/50 text-amber-700 dark:text-amber-300 font-bold whitespace-nowrap">NUEVO</span>
                        )}
                      </div>
                      <p className="text-base text-gray-500 dark:text-gray-400 truncate mt-0.5">{n.lastMessageText || n.phone}</p>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                      <span className="text-sm text-gray-400 whitespace-nowrap">{timeAgo(n.lastMessageTime)}</span>
                      {n.assignedAgent && !own && (
                        <span className="text-xs text-gray-400 truncate max-w-[80px]">{n.assignedAgent}</span>
                      )}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
